/**
 * CollectionGroupDetailPage - Public view of a single collection group with its artifacts
 * Elvis Aloha Collection Archive
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Layers, BookOpen, Package, Archive, Image, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Group type display config
const GROUP_TYPE_CONFIG = {
  curated: { label: 'Curated Collection', icon: Layers, color: 'text-amber-700', bg: 'bg-amber-50' },
  exhibition: { label: 'Exhibition', icon: BookOpen, color: 'text-blue-700', bg: 'bg-blue-50' },
  category: { label: 'Category', icon: Package, color: 'text-stone-600', bg: 'bg-stone-50' },
  research: { label: 'Research', icon: Archive, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  acquisition: { label: 'Acquisition', icon: Archive, color: 'text-stone-500', bg: 'bg-stone-50' },
};

// Format currency
const formatUSD = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

function ArtifactCard({ artifact }) {
  const primaryImage = artifact.primary_image?.thumbnail_url || artifact.primary_image?.url;
  
  return (
    <Link
      to={`/collection/items/${artifact.slug}`}
      className="group block bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-amber-300"
      data-testid={`artifact-card-${artifact.slug}`}
    >
      {/* Image */}
      <div className="aspect-square bg-stone-100 relative overflow-hidden">
        {primaryImage ? (
          <img
            src={primaryImage.startsWith('/') ? `${API_BASE}${primaryImage}` : primaryImage}
            alt={artifact.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-12 h-12 text-stone-300" />
          </div>
        )}
        
        {/* Featured Badge */}
        {artifact.is_featured_in_group && (
          <div className="absolute top-3 left-3 bg-amber-600 text-white text-xs font-medium px-2 py-1 rounded-full">
            Featured
          </div>
        )}
        
        {/* Status Badge */}
        {artifact.public_status_display && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
            {artifact.public_status_display}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="text-xs text-stone-500 mb-1">{artifact.inventory_number}</div>
        <h3 className="font-semibold text-stone-900 group-hover:text-amber-700 transition-colors line-clamp-2 mb-2">
          {artifact.title}
        </h3>
        
        {artifact.show_appraised_value && artifact.appraised_value_usd && (
          <div className="text-amber-700 font-medium">
            {formatUSD(artifact.appraised_value_usd)}
          </div>
        )}
        
        {artifact.short_description && (
          <p className="text-sm text-stone-500 line-clamp-2 mt-2">
            {artifact.short_description}
          </p>
        )}
      </div>
    </Link>
  );
}

function Pagination({ page, pages, onPageChange }) {
  if (pages <= 1) return null;
  
  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="p-2 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <span className="px-4 py-2 text-sm text-stone-600">
        Page {page} of {pages}
      </span>
      
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= pages}
        className="p-2 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

export default function CollectionGroupDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchGroup() {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/collection-groups/public/${slug}?page=${page}&per_page=20`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Collection not found');
          }
          throw new Error('Failed to load collection');
        }
        const data = await response.json();
        setGroup(data.group);
        setArtifacts(data.artifacts || []);
        setTotalPages(data.pages || 1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (slug) {
      fetchGroup();
    }
  }, [slug, page]);
  
  const typeConfig = group ? (GROUP_TYPE_CONFIG[group.group_type] || GROUP_TYPE_CONFIG.curated) : null;
  const TypeIcon = typeConfig?.icon || Layers;
  
  if (loading && !group) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-700" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-stone-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-stone-900 mb-2">{error}</h1>
            <p className="text-stone-600 mb-6">The collection you're looking for could not be found.</p>
            <Link
              to="/collection/groups"
              className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Collections
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-stone-50">
      
      {/* Hero Section */}
      <div className="bg-white border-b border-stone-200">
        {group?.hero_image_url && (
          <div className="h-64 md:h-80 relative overflow-hidden">
            <img
              src={group.hero_image_url}
              alt={group.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-stone-500 hover:text-amber-700">Home</Link>
              </li>
              <li className="text-stone-400">/</li>
              <li>
                <Link to="/collection/groups" className="text-stone-500 hover:text-amber-700">Collections</Link>
              </li>
              <li className="text-stone-400">/</li>
              <li className="text-stone-900 font-medium">{group?.name}</li>
            </ol>
          </nav>
          
          {/* Group Header */}
          <div className="flex items-start gap-4">
            {typeConfig && (
              <div className={`p-3 rounded-lg ${typeConfig.bg}`}>
                <TypeIcon className={`w-6 h-6 ${typeConfig.color}`} />
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm mb-2">
                <span className={`${typeConfig?.color || 'text-stone-600'} font-medium`}>
                  {typeConfig?.label || 'Collection'}
                </span>
                {group?.is_featured && (
                  <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    Featured
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                {group?.name}
              </h1>
              
              {group?.short_description && (
                <p className="text-lg text-stone-600 max-w-3xl">
                  {group.short_description}
                </p>
              )}
              
              <div className="mt-4 text-sm text-stone-500">
                {group?.artifact_count || 0} {group?.artifact_count === 1 ? 'artifact' : 'artifacts'} in this collection
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Description & Historical Overview */}
        {(group?.description || group?.historical_overview) && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 mb-8">
            {group.description && (
              <div className="prose prose-stone max-w-none mb-6">
                <h2 className="text-xl font-semibold text-stone-900 mb-3">About This Collection</h2>
                <p className="text-stone-600 whitespace-pre-wrap">{group.description}</p>
              </div>
            )}
            
            {group.historical_overview && (
              <div className="prose prose-stone max-w-none border-t border-stone-100 pt-6">
                <h2 className="text-xl font-semibold text-stone-900 mb-3">Historical Context</h2>
                <p className="text-stone-600 whitespace-pre-wrap">{group.historical_overview}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Artifacts Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700" />
          </div>
        ) : artifacts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
            <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">No artifacts in this collection yet.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {artifacts.map(artifact => (
                <ArtifactCard key={artifact.id} artifact={artifact} />
              ))}
            </div>
            
            <Pagination page={page} pages={totalPages} onPageChange={setPage} />
          </>
        )}
        
        {/* Back Link */}
        <div className="mt-12 text-center">
          <Link
            to="/collection/groups"
            className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to All Collections
          </Link>
        </div>
      </div>
    </div>
  );
}
